// src/components/common/Pager.jsx
import React, { useMemo } from 'react';
import Button from './Button';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Props:
 *  - pageSize: number
 *  - setPageSize: (n: number) => void
 *  - currentPage: number (1-based)
 *  - setCurrentPage: (fn | number) => void
 *  - totalRows: number
 *  - pageSizeOptions?: number[] (default: [10, 20, 50])
 *  - className?: string
 */
export default function Pager({
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalRows,
  pageSizeOptions = [10, 20, 50],
  className = '',
}) {
  const { totalPages, showingFrom, showingTo, clampedPage } = useMemo(() => {
    const totalPagesCalc = Math.max(1, Math.ceil((totalRows || 0) / (pageSize || 1)));
    const clamped = Math.min(Math.max(1, currentPage || 1), totalPagesCalc);
    const from = totalRows === 0 ? 0 : (clamped - 1) * pageSize + 1;
    const to = Math.min(clamped * pageSize, totalRows);
    return {
      totalPages: totalPagesCalc,
      showingFrom: from,
      showingTo: to,
      clampedPage: clamped,
    };
  }, [totalRows, pageSize, currentPage]);

  const isPrevDisabled = clampedPage <= 1;
  const isNextDisabled = clampedPage >= totalPages;

  return (
    <div className={`px-4 py-3 border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}>
      {/* Left: Rows per page */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">Rows per page:</span>
        <select
          className="px-2 py-1 border border-gray-300 rounded-md text-sm"
          value={pageSize}
          onChange={(e) => {
            const next = Number(e.target.value);
            setPageSize(next);
            // Reset to first page whenever page size changes
            setCurrentPage(1);
          }}
        >
          {pageSizeOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}{opt === 20 ? ' (recommended)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Center: Showing X–Y of Z */}
      <div className="text-sm text-gray-700 text-center">
        Showing <span className="font-medium">{showingFrom}</span> to{' '}
        <span className="font-medium">{showingTo}</span> of{' '}
        <span className="font-medium">{totalRows}</span> results
      </div>

      {/* Right: Pager */}
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage((p) => Math.max(1, (typeof p === 'number' ? p : clampedPage) - 1))}
          disabled={isPrevDisabled}
        >
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Prev
        </Button>
        <span className="text-sm text-gray-700">
          Page <span className="font-medium">{clampedPage}</span> / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage((p) => Math.min(totalPages, (typeof p === 'number' ? p : clampedPage) + 1))}
          disabled={isNextDisabled}
        >
          Next
          <ChevronRightIcon className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
